import { SalesLeadView } from "src/api/generated/erp/db/types/tables/salesLeadView.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import {
	faCommentDollar,
	faHandshake,
	faThumbsDown,
	faTimesCircle,
	faUndo,
	faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { SalesLeadsApi } from "src/api/generated/erp/sales/crm/salesLeads/api/salesLeadsApi.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SalesLeadToOfferWizard } from "src/components/views/erp/sales/crm/salesLeadToOffer/SalesLeadToOfferWizard.tsx";
import { SingleCustomerOfferView } from "src/components/views/erp/sales/customerOrderOffer/SingleCustomerOfferView.tsx";
import {
	SalesLeadAllocationDialogResult,
	useSalesLeadAllocationDialog,
} from "src/components/views/erp/sales/crm/salesLeads/useSalesLeadAllocationDialog.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesLeadsDataGridRowContextMenuProps {
	row: SalesLeadView;
	allSelectedRows: SalesLeadView[];
	refreshData: () => Promise<unknown>;
	onSalesLeadActivitiesChanged?: () => Promise<unknown> | void;
	onSalesLeadOffersChanged?: () => Promise<unknown> | void;
	editable?: boolean;
}

export const SalesLeadsDataGridRowContextMenu = (props: SalesLeadsDataGridRowContextMenuProps) => {
	const { row, allSelectedRows, refreshData, onSalesLeadActivitiesChanged, onSalesLeadOffersChanged, editable = true } =
		props;
	const onlySingleRowSelected = allSelectedRows.length === 1;

	const showInputDialog = useInputDialog();
	const showSalesLeadAllocationDialog = useSalesLeadAllocationDialog();
	const hasPermission = useHasPermission();
	const { openDialog } = useGenericDialog();

	const customerOrderOfferId = row.customerOrderOfferId;
	return [
		onlySingleRowSelected && customerOrderOfferId != null && (
			<AsyncMenuButton
				key={"openOffer"}
				label={i18n.t("open_offer")}
				icon={faCommentDollar}
				onClick={() => {
					openOfferDialog(customerOrderOfferId);
				}}
			/>
		),
		editable && onlySingleRowSelected && row.salesLeadState === "OPEN" && customerOrderOfferId == null && (
			<AsyncMenuButton
				key={"createOffer"}
				label={i18n.t("create_offer")}
				icon={faHandshake}
				onClick={() => onCreateOfferClick(row.salesLeadId)}
			/>
		),
		editable && allSelectedRows.some((r) => r.salesLeadState === "OPEN") && (
			<AsyncMenuButton
				key={"discard"}
				label={i18n.t("discard_lead")}
				icon={faTimesCircle}
				onClick={async () => {
					await onDiscardClick(allSelectedRows);
					await refreshData();
				}}
			/>
		),
		editable && allSelectedRows.some((r) => r.salesLeadState === "OPEN") && (
			<AsyncMenuButton
				key={"markAsLost"}
				label={i18n.t("mark_as_lost")}
				icon={faThumbsDown}
				onClick={async () => {
					await onMarkAsLostClick(allSelectedRows);
					await refreshData();
				}}
			/>
		),
		editable && allSelectedRows.some((r) => r.salesLeadState !== "OPEN") && (
			<AsyncMenuButton
				key={"reopen"}
				label={i18n.t("reopen")}
				icon={faUndo}
				onClick={async () => {
					await onReopenClick(allSelectedRows);
					await refreshData();
				}}
			/>
		),
		editable && hasPermission(Permissions.Sales.SalesLeads.ALLOCATE) && (
			<AsyncMenuButton
				key={"allocate"}
				label={i18n.t("allocate_lead")}
				icon={faUserPlus}
				onClick={async () => {
					const allocation = await onAllocateClick(allSelectedRows);
					if (allocation == null) return;
					await Promise.all([
						refreshData(),
						allocation.moveOpenActivities ? onSalesLeadActivitiesChanged?.() : undefined,
					]);
				}}
			/>
		),
	];

	async function onDiscardClick(rows: SalesLeadView[]) {
		const openRows = rows.filter((r) => r.salesLeadState === "OPEN");
		if (openRows.length === 0) return;
		const discardedReason = await showInputDialog({
			type: "string",
			title: i18n.t("discard_lead"),
			fieldLabel: i18n.t("discard_reason"),
			defaultValue: "",
			fieldProps: {
				text: {
					multiLine: true,
				},
			},
		});
		if (discardedReason === undefined) return;
		await SalesLeadsApi.discard({
			salesLeadIds: openRows.map((r) => r.salesLeadId),
			discardedReason: discardedReason,
		});
	}

	async function onMarkAsLostClick(rows: SalesLeadView[]) {
		const openRows = rows.filter((r) => r.salesLeadState === "OPEN");
		if (openRows.length === 0) return;
		const lostReason = await showInputDialog({
			type: "string",
			title: i18n.t("mark_as_lost"),
			fieldLabel: i18n.t("lose_reason"),
			defaultValue: "",
			fieldProps: {
				text: {
					multiLine: true,
				},
			},
		});
		if (lostReason === undefined) return;
		await SalesLeadsApi.markAsLost({
			salesLeadIds: openRows.map((r) => r.salesLeadId),
			lostReason: lostReason,
		});
	}

	async function onReopenClick(rows: SalesLeadView[]) {
		const nonOpenRows = rows.filter((r) => r.salesLeadState !== "OPEN");
		if (nonOpenRows.length === 0) return;
		await SalesLeadsApi.reopen({
			salesLeadIds: nonOpenRows.map((r) => r.salesLeadId),
		});
	}

	async function onAllocateClick(rows: SalesLeadView[]): Promise<SalesLeadAllocationDialogResult | undefined> {
		const allocation = await showSalesLeadAllocationDialog();
		if (allocation == null) return undefined;

		await SalesLeadsApi.allocate({
			salesLeadIds: rows.map((r) => r.salesLeadId),
			salespersonId: allocation.salespersonId,
			moveOpenActivities: allocation.moveOpenActivities,
		});
		return allocation;
	}

	function onCreateOfferClick(salesLeadId: number) {
		openDialog(({ closeDialog, onContentEdited }) => ({
			title: i18n.t("create_offer"),
			size: "xl",
			content: (
				<SalesLeadToOfferWizard
					salesLeadId={salesLeadId}
					onFormEdited={onContentEdited}
					onCompleted={async (result) => {
						await closeDialog();
						await Promise.all([refreshData(), onSalesLeadOffersChanged?.()]);
						openOfferDialog(result.customerOrderOfferId);
					}}
					onClose={() => closeDialog({ confirmIfEdited: true })}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.crm.salesLeadToOffer.dialog"),
				title: i18n.t("create_offer"),
				pathSegments: [i18n.t("create_offer")],
				data: { salesLeadId },
			},
		}));
	}

	function openOfferDialog(customerOrderOfferId: number) {
		openDialog(() => ({
			title: i18n.t("offer"),
			content: <SingleCustomerOfferView customerOrderOfferId={customerOrderOfferId} />,
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrderOffer.salesLeadsDataGridRowContextMenu.dialog"),
				title: i18n.t("offer"),
				pathSegments: [`${i18n.t("offer")} ${customerOrderOfferId}`],
				data: { customerOrderOfferId },
			},
		}));
	}
};
